import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';
import { ContentFilterRssView, ContentFilterRssViewProps } from './ContentFilterRss.view';
import { UseFormReturn } from 'react-hook-form';
import { FindContentContext } from '../FindContentProvider';
import { getApolloErrorMessage, remapApolloErrors } from '../../../../helpers/remapApolloErrors';
import { ApolloError } from '@apollo/client';
import { fetchRssParameters } from './ContentFilterRss.apollo';
import { TreeMenuContext } from '../TreeMenu/TreeMenuProvider';
import { ValidationMessages } from '../../../../constants/validation-messages';
import { isValidUrl } from 'helpers/urlHelpers';
import { ImportSourceArgsGroupInput } from '../../../../models/graphql';
import { trim } from 'lodash';
import { useDidMount } from '../../../../hooks/useDidMount';

export type ContentFilterRssControllerProps = {
  formInstance: UseFormReturn;
  searchParameters: fetchRssParameters | undefined;
  setSearchParameters: Dispatch<SetStateAction<fetchRssParameters | undefined>>;
  fetchRssPosts: (parameters: fetchRssParameters) => Promise<void>;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  purgeRssCache: () => void;
};

export const ContentFilterRssController = (props: ContentFilterRssControllerProps): JSX.Element => {
  const { fetchRssPosts, formInstance, purgeRssCache, setShowFilters, setSearchParameters } = props;
  const [showSaveSourceModal, setShowSaveSourceModal] = useState<boolean>(false);
  const { posts, setError, setSourceType, setSelectedPosts, setHasSearched } = useContext(FindContentContext);
  const { modalType, selectedMenuItem, setSelectedMenuItem } = useContext(TreeMenuContext);
  const [apolloErrorKey, setApolloErrorKey] = useState<string | undefined>(undefined);

  const clearRssForm = useCallback(
    (excluded: string) => {
      const elementNames = ['url', 'term'];

      setShowFilters(false);

      elementNames
        .filter(n => n !== excluded)
        .forEach(e => {
          formInstance.setValue(e, null);
        });
    },
    [formInstance, setShowFilters]
  );

  const getRssPosts = useCallback(
    (url: string, term: string) => {
      purgeRssCache();

      const parameters: fetchRssParameters = {
        url,
        term
      };

      setSearchParameters(parameters);

      fetchRssPosts(parameters)
        .then(() => {
          setShowFilters(true);
          setError(undefined);
          setApolloErrorKey(undefined);
          setHasSearched(true);
        })
        .catch((error: ApolloError) => {
          const apolloErrorType = getApolloErrorMessage(error);
          const remappedError = remapApolloErrors(error as ApolloError);

          // an invalid resource means that we should not be showing filters at all
          if (apolloErrorType === 'EXTERNAL_RESOURCE_INVALID') {
            setShowFilters(false);
          }

          setApolloErrorKey(apolloErrorType);
          setError(remappedError);

          purgeRssCache();
        });
    },
    [fetchRssPosts, purgeRssCache, setError, setHasSearched, setSearchParameters, setShowFilters]
  );

  const submitFormHandler = useCallback(
    (clearFilterBar?: boolean) => {
      const urlValue = (formInstance.getValues('url') || '').trim();
      const termValue = (formInstance.getValues('term') || '').trim();

      if (urlValue !== formInstance.getValues('url')) {
        formInstance.setValue('url', urlValue);
      }
      if (termValue !== formInstance.getValues('term')) {
        formInstance.setValue('term', termValue);
      }

      // Do not process if not a valid url
      if (!isValidUrl(urlValue)) {
        formInstance.control.setError('url', { message: ValidationMessages.discover.rssUrl.required });
        return;
      }

      if (clearFilterBar) {
        formInstance.resetField('term');
        getRssPosts(urlValue, '');
      } else if (urlValue) {
        formInstance.trigger('url').then(result => {
          if (result) {
            getRssPosts(urlValue, termValue);
          }
        });
      }
    },
    [formInstance, getRssPosts]
  );

  const getRssInputArgs = useCallback((): ImportSourceArgsGroupInput => {
    const requiredArgs = {
      url: trim(formInstance.getValues('url')),
      term: formInstance.getValues('term') ?? ''
    };

    const args = {
      ...requiredArgs
    };

    return { rss: args };
  }, [formInstance]);

  // on unMount, reset form
  useEffect(() => {
    return () => {
      formInstance.resetField('url');
      formInstance.resetField('term');
    };
  }, [formInstance]);

  useEffect(() => {
    if (
      !modalType &&
      selectedMenuItem &&
      selectedMenuItem.__typename === 'ImportSource' &&
      selectedMenuItem.args.__typename === 'ImportSourceArgsRss'
    ) {
      purgeRssCache();

      const { args } = selectedMenuItem;
      setError(undefined);

      const parameters: fetchRssParameters = {
        url: args.url ?? '',
        term: args.term ?? ''
      };

      formInstance.reset({
        platform: 'RSS',
        ...parameters
      });

      fetchRssPosts(parameters)
        .then(() => {
          setSearchParameters(parameters);
          setShowFilters(true);

          setSelectedMenuItem(undefined);
        })
        .catch(error => {
          setError(remapApolloErrors(error as ApolloError));
          setShowFilters(false);
        });
    }
  }, [
    fetchRssPosts,
    formInstance,
    modalType,
    purgeRssCache,
    selectedMenuItem,
    setError,
    setSearchParameters,
    setSelectedMenuItem,
    setShowFilters
  ]);

  useDidMount(() => {
    setError(undefined);
    setHasSearched(false);
    setApolloErrorKey(undefined);
    setSourceType(undefined);

    return () => {
      setSourceType(undefined);
      setSelectedPosts([]);
      clearRssForm('');
      purgeRssCache();
    };
  });

  const viewProps: ContentFilterRssViewProps = {
    ...props,
    hasPosts: posts.length > 0,
    submitFormHandler,
    showSaveSourceModal,
    setShowSaveSourceModal,
    getRssInputArgs,
    apolloErrorKey
  };

  return <ContentFilterRssView {...viewProps} />;
};
